import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import styles from './PromotionCard.module.scss';
import { DynamicImageLoader } from '../DynamicImageLoader';
import Typography from '../Typography/Typography';
import { useButtonVariant } from '../Button/useButtonVariant';
import { Button, Link } from '~/shared/components';
import type { TPromotionCard } from './PromotionCard.definition';
import { useFrame } from '~/shared/utils';
import { useEvents } from '~/shared/hooks/useEvents';
import { PROMOTION_EVENT_TYPE, type PromotionEventData } from '~/shared/hooks/useEvents/models';

export const PromotionCard = ({
    trumpet,
    image_url,
    cta_text,
    cta_type,
    title,
    redirect_url,
    alt_text,
    objectID,
    position,
    name,
    listName,
}: TPromotionCard) => {
    const { data: frame } = useFrame();
    const { promotionClickEvent, promotionViewEvent } = useEvents(frame);
    const { inView, ref } = useInView({ threshold: 0.5, triggerOnce: true });
    const buttonVariation = useButtonVariant(cta_type ?? 'Default', '#000');
    const isContentAvailable = Boolean(trumpet || title || cta_text);
    const isCTAShown = cta_text;
    const promotionTrackingDetails: PromotionEventData = {
        id: objectID,
        type: PROMOTION_EVENT_TYPE.PROMOTIONAL_CONTENT,
        position,
        title,
        name,
        ctaText: cta_text,
        category: listName,
    };

    const promotionClickHandler = () => {
        promotionClickEvent(promotionTrackingDetails);
    };

    useEffect(() => {
        if (!inView) return;

        promotionViewEvent(promotionTrackingDetails);
    }, [inView]);

    const promotionContent = (
        <>
            <DynamicImageLoader
                src={image_url}
                // empty alt text if the image is only decorative
                alt={isCTAShown ? '' : alt_text}
                fill
                style={{ objectFit: 'cover' }}
            />
            {isContentAvailable && (
                <div className={styles.contentWrapper}>
                    {trumpet && (
                        <Typography as="p" variant="paragraph-S" className={styles.trumpet}>
                            {trumpet}
                        </Typography>
                    )}
                    {title && <Typography variant="title-XXS">{title}</Typography>}
                    {isCTAShown && (
                        <Button
                            as={Link}
                            aria-label={alt_text}
                            variant={buttonVariation}
                            href={redirect_url}
                            onClick={promotionClickHandler}
                            className={buttonVariation === 'new-link' ? styles.link : ''}
                        >
                            {cta_text}
                        </Button>
                    )}
                </div>
            )}
        </>
    );

    return (
        <div ref={ref} className={styles.promotionCardWrapper}>
            {isCTAShown ? (
                promotionContent
            ) : (
                <Link
                    className={styles.cardLink}
                    onClick={promotionClickHandler}
                    noHover
                    href={redirect_url}
                >
                    {promotionContent}
                </Link>
            )}
        </div>
    );
};
